import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { inputCls } from "../../components/admin/fields";
import Logo from "../../components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper grid lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:flex flex-col justify-between bg-indigo-950 text-neutral-100 p-14">
        <div className="inline-flex items-center gap-3">
          <Logo tone="light" size="lg" testid="admin-login-logo" />
          <span className="eyebrow text-indigo-300">Editor</span>
        </div>
        <p className="font-playfair text-4xl leading-tight max-w-md">
          Useful first. <span className="italic text-indigo-300">Promotional second.</span>
        </p>
        <p className="text-sm text-neutral-400">Editorial workspace · Authorised staff only</p>
      </div>

      <div className="flex items-center px-6 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-sm mx-auto animate-fade-in-up" data-testid="admin-login-form">
          <div className="w-11 h-11 rounded-full bg-indigo-50 text-indigo-800 flex items-center justify-center mb-6">
            <Lock size={18} />
          </div>
          <h1 className="font-playfair text-4xl text-neutral-900 tracking-tight mb-2">Sign in</h1>
          <p className="text-sm text-neutral-600 mb-8">Publish and edit reviews, guides, and comparisons.</p>

          <label className="block eyebrow text-neutral-600 mb-2" htmlFor="admin-email">Email</label>
          <input id="admin-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className={`${inputCls} mb-5`} />

          <label className="block eyebrow text-neutral-600 mb-2" htmlFor="admin-password">Password</label>
          <input id="admin-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className={`${inputCls} mb-6`} />

          {error && (
            <p className="mb-5 text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="admin-login-error">
              {error}
            </p>
          )}

          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 disabled:opacity-60 transition-colors">
            {busy && <Loader2 size={15} className="animate-spin" />} Sign in
          </button>
        </form>
      </div>
    </div>
  );
}
